import React, { useEffect, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import * as XLSX from 'xlsx';
import { CrmBadge, CrmButton, CrmFormDrawer, CrmInput, CrmSelect, CrmSwitch, CrmTextarea, CrmTooltip } from '../ui';
import ConfirmDialog from '../ui/ConfirmDialog';
import MediaAssetPicker from '../settings/MediaAssetPicker';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import { CrmEntityListPage } from '../listing';
import { deleteStorefrontCategory, loadProductEditor, listStorefrontCategories, saveStorefrontCategory } from '../../services/productEditorService';
import './catalog-drawers.scss';

const slugify = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const emptyForm = {
  name: '',
  slug: '',
  parent_id: '',
  display_order: 0,
  is_active: true,
  shop_visible: true,
  build_box_visible: true,
  image_media_asset_id: '',
  image_url: '',
  meta_title: '',
  meta_description: '',
};

const exportCategories = (rows, filename, storefrontName) => {
  const worksheet = XLSX.utils.json_to_sheet(rows.map((category) => ({
    Storefront: storefrontName || '',
    Category: category.name || '',
    Parent: category.parent_name || '',
    Status: category.is_active ? 'Active' : 'Inactive',
    Order: Number(category.display_order || 0),
    Slug: category.slug || '',
  })));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Storefront Categories');
  XLSX.writeFile(workbook, filename);
};

export default function StorefrontCategoryList({ hideSavedViews = false, staticViews, activeStaticView, onStaticViewChange }) {
  const [storefronts, setStorefronts] = useState([]);
  const [storefrontId, setStorefrontId] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const currentStorefront = storefronts.find((item) => Number(item.id) === Number(storefrontId));

  const loadStorefronts = async () => {
    try {
      const result = await loadProductEditor();
      const next = result?.masterData?.storefronts || [];
      setStorefronts(next);
      if (!storefrontId && next[0]) setStorefrontId(String(next[0].id));
    } catch {
      setNotification({ open: true, message: 'Unable to load storefronts.', severity: 'error' });
    }
  };

  const loadCategories = async (selectedStorefront = currentStorefront) => {
    if (!selectedStorefront) {
      setCategories([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await listStorefrontCategories(selectedStorefront.code || selectedStorefront.id);
      setCategories(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setNotification({ open: true, message: error?.response?.data?.error || 'Unable to load storefront categories.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStorefronts(); }, []);
  useEffect(() => {
    const selected = storefronts.find((item) => Number(item.id) === Number(storefrontId));
    if (selected) loadCategories(selected);
  }, [storefrontId, storefronts.length]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDrawerOpen(true);
  };

  const openEdit = (category) => {
    setEditing(category);
    setForm({
      name: category.name || '',
      slug: category.slug || '',
      parent_id: category.parent_id || '',
      display_order: Number(category.display_order || 0),
      is_active: Boolean(category.is_active),
      shop_visible: Boolean(category.shop_visible),
      build_box_visible: Boolean(category.build_box_visible),
      image_media_asset_id: category.image_media_asset_id || '',
      image_url: category.image_url || '',
      meta_title: category.meta_title || '',
      meta_description: category.meta_description || '',
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    if (!currentStorefront || !form.name.trim() || saving) return;
    setSaving(true);
    try {
      const saved = await saveStorefrontCategory({
        storefront: currentStorefront.code || currentStorefront.id,
        id: editing?.id || null,
        payload: {
          name: form.name.trim(),
          slug: form.slug || slugify(form.name),
          parent_id: form.parent_id ? Number(form.parent_id) : null,
          display_order: Math.max(0, Math.round(Number(form.display_order) || 0)),
          is_active: Boolean(form.is_active),
          shop_visible: Boolean(form.shop_visible),
          build_box_visible: Boolean(form.build_box_visible),
          image_media_asset_id: form.image_media_asset_id || null,
          meta_title: form.meta_title || null,
          meta_description: form.meta_description || null,
        },
      });
      setCategories((previous) => {
        const next = editing
          ? previous.map((category) => Number(category.id) === Number(saved.id) ? saved : category)
          : [...previous, saved];
        return next.sort((left, right) => Number(left.display_order || 0) - Number(right.display_order || 0) || String(left.name || '').localeCompare(String(right.name || '')));
      });
      setDrawerOpen(false);
      setNotification({ open: true, message: editing ? 'Storefront category updated.' : 'Storefront category created.', severity: 'success' });
    } catch (error) {
      setNotification({ open: true, message: error?.response?.data?.error || 'Failed to save storefront category.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!currentStorefront || !deleteTarget.length) return;
    try {
      for (const categoryId of deleteTarget) {
        await deleteStorefrontCategory({ storefront: currentStorefront.code || currentStorefront.id, id: categoryId });
      }
      const deleted = new Set(deleteTarget.map(Number));
      setCategories((previous) => previous.filter((category) => !deleted.has(Number(category.id))));
      setNotification({ open: true, message: `${deleteTarget.length} storefront categor${deleteTarget.length === 1 ? 'y' : 'ies'} deleted.`, severity: 'success' });
      setConfirmOpen(false);
      setDeleteTarget([]);
    } catch (error) {
      setNotification({ open: true, message: error?.response?.data?.error || 'Failed to delete storefront category.', severity: 'error' });
    }
  };

  const parentNameById = useMemo(() => new Map(categories.map((item) => [Number(item.id), item.name])), [categories]);
  const rows = useMemo(() => categories.map((category) => ({
    ...category,
    parent_name: category.parent_id ? parentNameById.get(Number(category.parent_id)) || '—' : '—',
    storefront_name: currentStorefront?.name || '',
  })), [categories, currentStorefront?.name, parentNameById]);

  const columns = useMemo(() => [
    { id: 'name', label: 'Storefront Category', width: 280, minWidth: 100, hideable: false, pinnable: true },
    {
      id: 'parent_id', label: 'Parent', width: 180, minWidth: 100,
      accessor: (row) => row.parent_name || '—',
    },
    {
      id: 'is_active', label: 'Status', width: 120, minWidth: 100, type: 'boolean',
      accessor: (row) => Boolean(row.is_active),
      renderCell: (row) => <CrmBadge tone={row.is_active ? 'success' : 'neutral'}>{row.is_active ? 'Active' : 'Inactive'}</CrmBadge>,
    },
    {
      id: 'shop_visible', label: 'Shop', width: 100, minWidth: 84, type: 'boolean',
      accessor: (row) => Boolean(row.shop_visible),
      renderCell: (row) => <CrmBadge tone={row.shop_visible ? 'success' : 'neutral'}>{row.shop_visible ? 'Visible' : 'Hidden'}</CrmBadge>,
    },
    {
      id: 'build_box_visible', label: 'Build Box', width: 112, minWidth: 96, type: 'boolean',
      accessor: (row) => Boolean(row.build_box_visible),
      renderCell: (row) => <CrmBadge tone={row.build_box_visible ? 'success' : 'neutral'}>{row.build_box_visible ? 'Visible' : 'Hidden'}</CrmBadge>,
    },
    { id: 'display_order', label: 'Order', width: 110, minWidth: 100, type: 'number', accessor: (row) => Number(row.display_order || 0) },
    {
      id: 'row_actions', label: 'Actions', width: 104, minWidth: 100, hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (row) => <span className="crm-row-actions">
        <CrmTooltip content="Edit category"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => openEdit(row)} aria-label="Edit storefront category"><EditOutlinedIcon /></CrmButton></CrmTooltip>
        <CrmTooltip content="Delete category"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action crm-row-action--danger" onClick={() => { setDeleteTarget([row.id]); setConfirmOpen(true); }} aria-label="Delete storefront category"><DeleteOutlineIcon /></CrmButton></CrmTooltip>
      </span>,
    },
  ], []);

  const filters = useMemo(() => [
    { id: 'name', columnId: 'name', label: 'Category', type: 'text', quick: true },
    { id: 'parent_id', columnId: 'parent_id', label: 'Parent', type: 'text', quick: true },
    { id: 'is_active', columnId: 'is_active', label: 'Status', type: 'select', quick: true, options: [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ] },
  ], []);

  return <div className="contacts-page">
    <CrmEntityListPage
      moduleKey={`storefront-categories:${storefrontId || 'none'}`}
      title="Storefront Categories"
      itemLabel="storefront category"
      itemPluralLabel="storefront categories"
      items={rows}
      columns={columns}
      filterDefinitions={filters}
      loading={loading}
      onRefresh={() => loadCategories(currentStorefront)}
      onCreate={openCreate}
      createLabel="Add storefront category"
      onExport={(exportRows) => exportCategories(exportRows, 'storefront_categories.xlsx', currentStorefront?.name)}
      onExportSelected={(exportRows) => exportCategories(exportRows, 'selected_storefront_categories.xlsx', currentStorefront?.name)}
      onDeleteSelected={(ids) => { setDeleteTarget(ids); setConfirmOpen(true); }}
      headerActions={<CrmSelect aria-label="Storefront" value={storefrontId} onChange={(event) => setStorefrontId(event.target.value)}>{storefronts.map((storefront) => <option key={storefront.id} value={storefront.id}>{storefront.name}</option>)}</CrmSelect>}
      emptyTitle="No storefront categories yet."
      emptyMessage="Create customer-facing categories for this storefront."
      hideSavedViews={hideSavedViews}
      staticViews={staticViews}
      activeStaticView={activeStaticView}
      onStaticViewChange={onStaticViewChange}
    />

    <CrmFormDrawer
      open={drawerOpen}
      onClose={() => !saving && setDrawerOpen(false)}
      onSubmit={save}
      title={editing ? 'Edit Storefront Category' : 'Add Storefront Category'}
      subtitle={`Customer-facing category for ${currentStorefront?.name || 'the selected storefront'}.`}
      size="md"
      mode={editing ? 'edit' : 'create'}
      canSave={Boolean(currentStorefront && form.name.trim() && !saving)}
      saveLabel="Save Category"
    >
      <div className="crm-form-stack">
        <div className="storefront-category-toggle-row">
          <CrmSwitch label="Active" checked={form.is_active} onChange={(event) => setForm((previous) => ({ ...previous, is_active: event.target.checked }))} />
          <CrmSwitch label="Shop Visible" checked={form.shop_visible} onChange={(event) => setForm((previous) => ({ ...previous, shop_visible: event.target.checked }))} />
          <CrmSwitch label="Build Box Visible" checked={form.build_box_visible} onChange={(event) => setForm((previous) => ({ ...previous, build_box_visible: event.target.checked }))} />
        </div>
        <CrmInput required label="Name" value={form.name} onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value, slug: previous.slug || slugify(event.target.value) }))} />
        <CrmInput label="Slug" value={form.slug} onChange={(event) => setForm((previous) => ({ ...previous, slug: slugify(event.target.value) }))} />
        <CrmSelect label="Parent Category" value={form.parent_id || ''} onChange={(event) => setForm((previous) => ({ ...previous, parent_id: event.target.value }))}>
          <option value="">No parent</option>
          {categories.filter((item) => Number(item.id) !== Number(editing?.id)).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </CrmSelect>
        <CrmInput type="number" min="0" step="1" label="Display Order" value={form.display_order} onChange={(event) => setForm((previous) => ({ ...previous, display_order: Math.max(0, Math.round(Number(event.target.value) || 0)) }))} />
        <MediaAssetPicker label="Category Image" mediaType="IMAGE" storefrontId={storefrontId} value={form.image_media_asset_id || ''} currentUrl={form.image_url || ''} onChange={(value, asset) => setForm((previous) => ({ ...previous, image_media_asset_id: value || '', image_url: asset?.url || previous.image_url || '' }))} />
        <CrmInput label="SEO Title" value={form.meta_title} onChange={(event) => setForm((previous) => ({ ...previous, meta_title: event.target.value }))} />
        <CrmTextarea label="SEO Description" rows={4} value={form.meta_description} onChange={(event) => setForm((previous) => ({ ...previous, meta_description: event.target.value }))} />
      </div>
    </CrmFormDrawer>

    <ConfirmDialog
      open={confirmOpen}
      title="Delete storefront category?"
      message={`Delete ${deleteTarget.length} storefront categor${deleteTarget.length === 1 ? 'y' : 'ies'}? Product/category assignments that reference the deleted category are removed automatically.`}
      confirmText="Delete"
      onCancel={() => { setConfirmOpen(false); setDeleteTarget([]); }}
      onConfirm={confirmDelete}
    />

    <NotificationSnackbar {...notification} onClose={() => setNotification((previous) => ({ ...previous, open: false }))} />
  </div>;
}
